import { Box, Button, FormControl, Grid, Typography } from '@mui/material'
import React, { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { IVerifyPage } from './types'
import TextInput from '../../atoms/TextInput'
import PoweredByBinarii from '../../atoms/PoweredByBinarii'

const VerifyPage: React.FC<IVerifyPage> = ({ resendCode, onVerifyFormSubmit, email }) => {
  const [code, setCode] = useState<string>('')
  const [timeLeft, setTimeLeft] = useState<number>(55) // 55 seconds countdown
  const [isExpired, setIsExpired] = useState<boolean>(false)
  const navigate = useNavigate()

  // Countdown timer effect
  useEffect(() => {
    if (timeLeft > 0) {
      const timer = setTimeout(() => {
        setTimeLeft(timeLeft - 1)
      }, 1000)
      return () => clearTimeout(timer)
    }
    setIsExpired(true)

    return undefined // Explicit return for consistency
  }, [timeLeft])

  // Format time as MM:SS
  const formatTime = (seconds: number): string => {
    const minutes = Math.floor(seconds / 60)
    const remainingSeconds = seconds % 60
    return `${minutes}:${remainingSeconds.toString().padStart(2, '0')}`
  }

  const onCodeChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setCode(e.target.value)
  }

  const onVerifyCodeFormSubmit = async (e: React.SyntheticEvent) => {
    e.preventDefault()
    if (code) {
      onVerifyFormSubmit(code)
    }
  }

  const handleResendCode = () => {
    resendCode()
    setTimeLeft(55) // Reset timer
    setIsExpired(false)
  }

  const handleBackToLogin = () => {
    navigate('/login')
  }

  return (
    <Grid sx={{ height: '85vh', bgcolor: 'background.paper' }}>
      <Grid height="100%" container justifyContent="center" alignItems="center">
        <Grid
          item
          className="login-inner-container"
          sx={{ '&': { display: 'flex', flexDirection: 'column', alignItems: 'center' } }}
        >
          <Box className="code-verify-box">
            <Typography
              color="#09090B"
              align="center"
              sx={{
                fontFamily: 'Typography/font/geist-medium',
                fontSize: '28px', // Typography/Scale/Display 1000
                fontWeight: 500,
                lineHeight: '100%',
                letterSpacing: '-3%',
                mb: 0.5, // Reduced margin for tighter spacing
              }}
            >
              Verify Account
            </Typography>
            <Typography color="#777777" align="center" sx={{ fontSize: '1.4rem', mb: 2 }}>
              Please enter the verification code we sent
              <br />
              to your inbox {email}
            </Typography>

            <FormControl sx={{ width: '100%' }}>
              <Typography
                sx={{
                  fontFamily: 'Geist, Arial, sans-serif',
                  fontSize: 'body-md-14',
                  fontWeight: 500,
                  color: '#1F1F1F',
                  letterSpacing: '0px',
                  lineHeight: '19px',
                  fontStyle: 'medium',
                }}
              >
                Code
              </Typography>
              <TextInput
                variant="outlined"
                value={code}
                onChange={onCodeChange}
                placeholder="Enter code"
                type="text"
                id="code-id"
                aria-describedby="code-helper-text"
                fullWidth
                autoComplete="code"
              />
            </FormControl>

            <Button
              variant="contained"
              color="purpleTheme"
              sx={{
                fontSize: 'body-md-14',
                mt: '1rem',
                mb: 1.5,
                display: 'flex',
                justifyContent: 'center',
                borderRadius: '.5rem',
                width: '100%',
                '&.Mui-disabled': {
                  backgroundColor: '#E2CDFF', // Light gray for disabled state
                  color: '#FFFFFF', // Darker gray text for disabled state
                },
              }}
              type="submit"
              onClick={onVerifyCodeFormSubmit}
              disabled={!(code.length >= 1)} // Disable when no code is entered
            >
              Submit
            </Button>

            {/* Timer or Expired Status */}
            <Box sx={{ width: '100%', display: 'flex', justifyContent: 'center', mb: 1.5 }}>
              {!isExpired ? (
                <Typography
                  sx={{
                    fontSize: '1.4rem',
                    color: '#546473',
                  }}
                >
                  Code expires in <span style={{ color: '#FF6B6B' }}>{formatTime(timeLeft)}</span>
                </Typography>
              ) : (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Button
                    onClick={handleResendCode}
                    sx={{ textTransform: 'none', p: 0, minWidth: 'auto' }}
                  >
                    <Typography
                      sx={{
                        fontSize: '1.4rem',
                        color: '#546473',
                        textDecoration: 'underline',
                      }}
                    >
                      Request code
                    </Typography>
                  </Button>
                  <Typography
                    sx={{
                      fontSize: '1.4rem',
                      color: '#FF6B6B',
                    }}
                  >
                    Code expired
                  </Typography>
                </Box>
              )}
            </Box>

            {/* Back to Login */}
            <Box sx={{ width: '100%', display: 'flex', justifyContent: 'center' }}>
              <Button onClick={handleBackToLogin} sx={{ textTransform: 'none' }}>
                <Typography
                  sx={{
                    fontSize: '1.4rem',
                    fontWeight: 500,
                    color: '#546473',
                    textDecoration: 'underline',
                  }}
                >
                  Back to Login
                </Typography>
              </Button>
            </Box>
          </Box>
        </Grid>
      </Grid>
      <Grid sx={{ height: '15vh', bgcolor: 'background.paper' }}>
        <PoweredByBinarii />
      </Grid>
    </Grid>
  )
}

export default VerifyPage
